import { RARITY_IDS, Rarity } from "@highmarch/domain";
import type { BalanceData, GearItem } from "@highmarch/domain";
import type { WyrmDef } from "../rare/wyrmApi";
import { bramStill } from "./guide/bram/bramView";
import { gearWellHtml } from "./hub/itemBlock";
import { motionOk } from "./kit";
import { chestIconUrl, resourceIconUrl, uiUrl } from "./portraits";
import { paintWyrm } from "./wyrmUi";
import { WYRM_COPY, WYRM_REVEAL, WyrmReveal } from "./wyrmModel";

/**
 * The Starfall Wyrm's prize, revealed the moment it falls (M40): a card in a prismatic frame bursting out of spinning
 * rays and confetti stars — the Starfall skin it unlocked (its render, "NEW SKIN UNLOCKED", its name, EQUIP to wear it
 * now) or, with the whole set owned, the hoard (a Mythic chest and the gems). Then it shrinks and flies into the HUD
 * plate. The battle runs on underneath: nothing here pauses the sim; a tap on the card (not its buttons) sends it on.
 */
export class WyrmRevealCard {
  private el: HTMLDivElement | null = null;
  private timers: number[] = [];

  constructor(private readonly parent: HTMLElement, private readonly target: () => DOMRect | null) {}

  /** `equip`: wears the skin (Cosmetics.equip through the skins link); true, or why not. */
  show(def: WyrmDef, plan: WyrmReveal, B: BalanceData | null, gear: readonly GearItem[], equip: ((skinId: string) => true | string) | null): void {
    this.hide();
    const el = document.createElement("div");
    el.className = `wyrm-reveal ${plan.kind}`;
    paintWyrm(el, def);
    const confetti = Array.from({ length: 18 }, (_, i) => `<i style="--i:${i}"></i>`).join("");
    el.innerHTML = `<div class="wr-rays"></div><div class="wr-confetti">${confetti}</div><div class="wr-card">${plan.kind === "skin" ? skinHtml(plan) : chestHtml(plan, B, gear)}`
      + `<div class="rb-bram wr-bram"><div class="rb-bust"></div><div class="rb-say">${plan.line}</div></div></div>`;
    el.querySelector(".rb-bust")?.append(bramStill("cheering", 60));
    el.addEventListener("click", (e) => { if (!(e.target as HTMLElement).closest("button")) this.fly(); });
    const eq = el.querySelector<HTMLButtonElement>(".wr-equip");
    if (eq && plan.kind === "skin") eq.onclick = () => this.equip(plan, equip, eq);
    el.querySelector<HTMLButtonElement>(".wr-later")?.addEventListener("click", () => this.fly());
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      el.querySelector<HTMLElement>(".wr-card")!.animate([{ transform: "translateY(80px) scale(0.3) rotate(-8deg)", opacity: 0 }, { transform: "translateY(-8px) scale(1.08) rotate(1deg)", opacity: 1, offset: 0.65 }, { transform: "none", opacity: 1 }], { duration: 640, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".wr-art")?.animate([{ transform: "scale(0.6) rotate(-14deg)", filter: "brightness(3)" }, { transform: "scale(1.12) rotate(3deg)", filter: "brightness(1.6)", offset: 0.6 }, { transform: "none", filter: "none" }], { duration: 900, delay: 300, easing: "ease-out", fill: "backwards" });
    }
    this.timers.push(window.setTimeout(() => this.fly(), WYRM_REVEAL.HoldMs));
  }

  private equip(plan: Extract<WyrmReveal, { kind: "skin" }>, equip: ((skinId: string) => true | string) | null, btn: HTMLButtonElement): void {
    const r = equip ? equip(plan.skinId) : "not here";
    const note = this.el?.querySelector<HTMLElement>(".wr-note");
    if (r !== true) { if (note) { note.textContent = WYRM_COPY.equipLater; note.classList.remove("hidden"); } return; }
    btn.disabled = true;
    btn.innerHTML = "<span>WORN</span>";
    if (note) { note.textContent = plan.wornNote; note.classList.remove("hidden"); }
    for (const t of this.timers) window.clearTimeout(t);
    this.timers = [window.setTimeout(() => this.fly(), WYRM_REVEAL.FlyMs * 2.4)];
  }

  /** Off into the HUD plate (or simply away when the plate is not on screen). */
  private fly(): void {
    const el = this.el;
    if (!el || el.classList.contains("flying")) return;
    el.classList.add("flying");
    const card = el.querySelector<HTMLElement>(".wr-card")!;
    const to = this.target(), from = card.getBoundingClientRect();
    if (!motionOk() || typeof card.animate !== "function" || !to) { this.hide(); return; }
    const dx = to.left + to.width / 2 - (from.left + from.width / 2), dy = to.top + to.height / 2 - (from.top + from.height / 2);
    for (const s of [".wr-rays", ".wr-confetti"]) el.querySelector<HTMLElement>(s)?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, fill: "forwards" });
    card.animate([{ transform: "none", opacity: 1 }, { transform: `translate(${dx * 0.4}px, ${dy * 0.4 - 30}px) scale(0.55)`, opacity: 1, offset: 0.45 }, { transform: `translate(${dx}px, ${dy}px) scale(0.1)`, opacity: 0.2 }], { duration: WYRM_REVEAL.FlyMs, easing: "cubic-bezier(.5,0,.8,.6)", fill: "forwards" })
      .onfinish = () => this.hide();
  }

  get shown(): boolean { return this.el !== null; }
  hide(): void { for (const t of this.timers) window.clearTimeout(t); this.timers = []; this.el?.remove(); this.el = null; }
}

function skinHtml(p: Extract<WyrmReveal, { kind: "skin" }>): string {
  return `<div class="wr-ribbon disp"><span>${p.title}</span></div><div class="wr-art" style="background-image:url('${uiUrl(p.art)}'), radial-gradient(ellipse 70% 55% at 50% 72%, rgba(255,201,168,0.5), transparent 70%)"><i class="wr-shine"></i></div>`
    + `<b class="wr-name disp">${p.name}</b><small class="wr-sub">${WYRM_COPY.revealSub}</small>`
    + `<div class="wr-acts"><button type="button" class="btn gold wr-equip"><span>${p.equipLabel}</span></button><button type="button" class="btn dark wr-later"><span>${WYRM_COPY.later}</span></button></div>`
    + `<small class="wr-note hidden"></small>`;
}

function chestHtml(p: Extract<WyrmReveal, { kind: "chest" }>, B: BalanceData | null, gear: readonly GearItem[]): string {
  const r = Math.max(0, Math.min(Rarity.Mythic, p.rarity)) as Rarity;
  const items = B && gear.length > 0 ? `<div class="wr-items">${gear.map((it) => gearWellHtml(B, it, "sm")).join("")}</div>` : "";
  return `<div class="wr-ribbon disp"><span>${p.title}</span></div><div class="wr-art chest"><img decoding="sync" src="${chestIconUrl(RARITY_IDS[r])}" alt=""><i class="wr-shine"></i></div>`
    + `<b class="wr-name disp">${RARITY_IDS[r].toUpperCase()} CHEST</b><span class="wr-gems"><img decoding="sync" src="${resourceIconUrl("gems")}" alt="gems">+${p.gems}</span>`
    + `<small class="wr-sub">${p.sub}</small>${items}`;
}
